<template>
    <li class="menu-item-li"
        :class="{ 'unavalible': item.available_for_order === 0 }">
        <div class="menu-item-wrap"
             :class="{ 'menu-item-wrap--no-photo': !item.image }"
             @click="openFullItem">
            <div v-if="item.image"
                 class="menu-item-image-wrap">
                <VImage :src="item.image"
                        :alt="item.name"
                        radius="18px" />

                <div v-if="item.available_for_order === 0"
                     class="menu-item-soldout">
                    {{ $t('pages.menu.soldOut') }}
                </div>
            </div>

            <div class="menu-item-info">
                <div class="menu-item-name">{{ item.name }}</div>
                <div class="menu-item-description">{{ item.description }}</div>
                <div class="menu-item-footer">
                    <span class="menu-item-price">
                        <span class="menu-item-price__value">{{ item.price }}</span>
                        <span class="menu-item-price__suffix">zł</span>
                    </span>
                    <span class="menu-item-icons">
                        <IconGluttenFreeOutline v-if="item.is_gluten_free"
                                                width="20"
                                                height="20"
                                                @click.native.stop="showIconDetails('gluten-free')" />
                        <IconPepperHot v-if="item.is_spicy"
                                       width="20"
                                       height="20"
                                       @click.native.stop="showIconDetails('chili-pepper')">
                        </IconPepperHot>
                        <IconVegetarian v-if="item.is_vegetarian"
                                        width="20"
                                        height="20"
                                        @click.native.stop="showIconDetails('vegeterian')">
                        </IconVegetarian>
                        <IconLowCal v-if="item.is_low_calorie"
                                    width="20"
                                    height="20"
                                    @click.native.stop="showIconDetails('low-calories')">
                        </IconLowCal>
                    </span>
                    <div class="menu-item-fav"
                         :class="{ 'is-active': isFavorite }"
                         role="button"
                         :aria-label="isFavorite ? $t('pages.menu.favorites.remove') : $t('pages.menu.favorites.add')"
                         @click.stop="toggleFavorite">
                        <IconStar v-if="isFavorite" />
                        <IconStarOutline v-else />
                    </div>
                    <div class="menu-item-add"
                         v-if="canOrder && item.available_for_order !== 0"
                         @click.stop="onAddClick">
                        <IconPlus />
                    </div>
                </div>
            </div>
        </div>
        <DishDetails v-if="showFullItem"
                     ref="detailsRef"
                     v-model="showFullItem"
                     :dish-id="item.id"
                     :dish="item" />
        <FoodIconInfoPopup ref="iconDetails"
                           :icon="activeIcon" />
    </li>
</template>

<script>
import IconGluttenFreeOutline from '@/components/icons/IconGluttenFreeOutline.vue';
import IconPepperHot from '@/components/icons/IconPepperHot.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconVegetarian from '@/components/icons/IconVegetarian.vue';
import IconLowCal from '@/components/icons/IconLowCal.vue';
import IconStar from '@/components/icons/IconStar.vue';
import IconStarOutline from '@/components/icons/IconStarOutline.vue';
import DishDetails from '@/modules/dishDetails/DishDetails.vue';
import FoodIconInfoPopup from "@/components/FoodIconInfoPopup.vue";
import VImage from "@/components/ui/VImage.vue";
import { isWithinSmartMenuWorkingHours } from "@/utils/scheduleHelper";

export default {
    name: "MenuItem",
    components: {
        IconGluttenFreeOutline,
        IconPepperHot,
        IconVegetarian,
        IconPlus,
        IconLowCal,
        IconStar,
        IconStarOutline,
        DishDetails,
        FoodIconInfoPopup,
        VImage,
    },
    props: {
        item: { type: Object, required: true },
    },
    data() {
        return {
            showFullItem: false,
            activeIcon: null,
            toastOptions: {
                position: "top-center",
                timeout: 3000,
                closeOnClick: true
            },
        };
    },
    computed: {
        canOrder() {
            const locale = this.$store.getters['getCompanyData'].locale;

            return (locale.activePaymentCredentials || this.$store.getters['getIsModeHOT'] || this.$store.getters['getIsModeSOT'])
                && locale.pos_terminal_enabled
                && locale.smart_menu_cart_enabled
                && isWithinSmartMenuWorkingHours(locale.smart_menu_schedule);
        },
        isWMM() {
            return this.$store.getters['getIsModeWMM'];
        },
        hasSelectableOptions() {
            // Options are only chosen in the detail sheet, and only when NOT in WMM
            // mode (WMM hides the options UI — see DishDetails). Any dish with
            // selectable options must open the sheet instead of quick-adding, even
            // if all its options are optional.
            return Array.isArray(this.item.options) && this.item.options.length > 0 && !this.isWMM;
        },
        isFavorite() {
            return this.$store.getters['favoritesStore/isFavorite'](this.item.id);
        },
    },
    methods: {
        toggleFavorite() {
            // On the main menu the toast (top-center) overlaps the header actions,
            // and the header star already pulses as feedback — so suppress it there.
            const onMainMenu = this.$route.name === 'menu';
            if (this.isFavorite) {
                this.$store.commit('favoritesStore/REMOVE_FAVORITE', this.item.id);
                if (!onMainMenu) {
                    this.$toast.success(this.$t('pages.menu.favorites.removed'), this.toastOptions);
                }
            } else {
                // Store a plain snapshot so the favorites screen can render the card
                // without holding a live reference to the menu's item object.
                this.$store.commit('favoritesStore/ADD_FAVORITE', { ...this.item });
                if (!onMainMenu) {
                    this.$toast.success(this.$t('pages.menu.favorites.added'), this.toastOptions);
                }
            }
        },
        openFullItem() {
            this.warmDishImage();
            this.showFullItem = true;
            this.$nextTick(() => {
                this.$refs.detailsRef.open();
            });
        },
        onAddClick() {
            // Quick-add straight to the cart when there is nothing to choose. If the
            // dish has selectable options (even optional ones), open the detail sheet
            // so the diner can pick them first.
            if (this.hasSelectableOptions) {
                this.openFullItem();
                return;
            }
            this.addToCartDirect();
        },
        addToCartDirect() {
            // Mirror DishDetails.addToCart for an options-less dish: no subproducts,
            // quantity 1, and a self-referencing `product` (the cart renders from it).
            const item = { ...this.item };
            item.subproducts = [];
            item.count = 1;
            item.product = { ...item };

            if (this.isWMM) {
                this.$store.dispatch('cartStore/SET_ITEM_TO_CART', item).then(resp => {
                    if (resp) {
                        this.$toast.success(this.$t('Added to cart'), this.toastOptions);
                    }
                });
            } else {
                this.$store.commit('cartStore/ADD_TO_CART', item);
                this.$toast.success(this.$t('Added to cart'), this.toastOptions);
            }
        },
        warmDishImage() {
            // The detail sheet reuses this card's exact image URL, so the bytes are
            // already cached — but its freshly-mounted <img> decodes asynchronously
            // (and at a larger size) and can flash blank while the sheet slides up.
            // Kick off an eager decode now so the decoded bitmap is ready by the time
            // the sheet paints. Fire-and-forget: never delay opening the sheet.
            const url = this.item && this.item.image;
            if (!url || typeof window === 'undefined' || typeof window.Image === 'undefined') return;
            const img = new window.Image();
            img.src = url;
            if (img.decode) {
                img.decode().catch(() => {});
            }
        },
        showIconDetails(icon) {
            this.activeIcon = icon;
            this.$nextTick(() => {
                this.$refs.iconDetails.open();
            });
        },
    }
};
</script>

<style lang="scss" scoped>
.slide-up-enter-active {
    transition: all .4s ease;
}

.slide-up-leave-active {
    transition: all .2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-up-enter,
.slide-fade-leave-to {
    transform: translateY(100vh);
    opacity: 0;
}

.menu-item-li {
    margin: 12px 0;

    border: 1px solid var(--border-subtle);
    border-radius: 24px;

    background: var(--surface-card);
    box-shadow: 0 6px 20px rgba(74, 55, 73, 0.06);

    &:last-of-type {
        margin-bottom: 0;
    }

    .menu-item-wrap {
        display: flex;
        gap: 14px;
        padding: 12px;
        cursor: pointer;
    }

    /* No-photo dishes degrade gracefully — text-first, no grey placeholder */
    .menu-item-wrap--no-photo {
        gap: 0;
        padding: 14px 16px;
    }
}

.unavalible {
    opacity: 0.55;
    pointer-events: none;
}

.menu-item-info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;

    .menu-item-name {
        margin-bottom: 4px;

        font-weight: var(--weight-bold);
        font-size: 16px;
        line-height: 20px;

        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;

        color: var(--text-primary);
    }

    .menu-item-description {
        margin-bottom: auto;

        font-size: 13px;
        line-height: 1.4;

        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;

        color: var(--text-secondary);
    }
}

.menu-item-footer {
    display: flex;
    align-items: center;
    gap: 10px;

    margin-top: 8px;

    .menu-item-price {
        display: inline-flex;
        align-items: baseline;
        gap: 3px;

        margin-right: auto;

        .menu-item-price__value {
            font-weight: var(--weight-black);
            font-size: 17px;
            line-height: 1;

            color: var(--text-primary);
        }

        .menu-item-price__suffix {
            font-weight: var(--weight-semibold);
            font-size: 13px;
            line-height: 1;

            color: var(--text-muted);
        }
    }

    .menu-item-icons {
        display: flex;
        gap: 6px;
    }

    .menu-item-fav {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        width: 34px;
        height: 34px;

        border: 1px solid var(--border-subtle);
        border-radius: 13px;

        background: var(--surface-card);
        color: var(--text-muted);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;

        transition: transform var(--dur-fast) var(--ease-standard),
                    color var(--dur-fast) var(--ease-standard),
                    border-color var(--dur-fast) var(--ease-standard);

        &:active {
            transform: scale(0.88);
        }

        &.is-active {
            border-color: var(--fav-star);
            color: var(--fav-star);
        }
    }

    .menu-item-add {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        width: 34px;
        height: 34px;

        border-radius: 13px;

        box-shadow: var(--shadow-brand);
        background: var(--grad-brand);

        ::v-deep svg path {
            fill: #fff;
        }
    }
}

.menu-item-image-wrap {
    position: relative;

    flex-shrink: 0;

    width: 104px;
    height: 104px;

    img {
        width: 100%;
        height: 100%;

        object-fit: cover;

        border-radius: 18px;
    }

    .menu-item-soldout {
        position: absolute;
        inset: 0;

        display: flex;
        align-items: center;
        justify-content: center;

        font-weight: var(--weight-bold);
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.04em;

        color: #fff;
        background: rgba(0, 0, 0, 0.45);
        border-radius: 18px;
    }
}

.menu-item-icon-popup {
    margin-bottom: 30px;
    padding: 0 30px 30px;

    text-align: center;
}
</style>
